import { FC, ReactNode, useCallback, useEffect, useState } from "react";

import {
  Button,
  makeStyles,
  Spinner,
  tokens,
} from "@fluentui/react-components";
import {
  CheckmarkCircleFilled,
  DismissCircleRegular,
  DismissRegular,
} from "@fluentui/react-icons";

import { translate } from "@/utils/translations";
import { DocumentStatus } from "@/pages/Thread/types";

interface ProcessingIndicatorProps {
  autoDismiss?: boolean;
  autoDismissDelay?: number;
  customIcon?: ReactNode;
  documentStatus?: DocumentStatus;
  message?: string;
  primaryActionLabel?: string;
  showDismissButton?: boolean;
  styles: {
    processingIndicator: string;
  };
  uploadError?: Error | null;
  variant?: ToastVariant;
  onAction?: () => void;
  onDismiss?: () => void;
  onRetry?: () => void;
}

type ToastVariant = "error" | "loading" | "success";

export const ProcessingIndicator: FC<ProcessingIndicatorProps> = ({
  autoDismiss,
  autoDismissDelay = 5000,
  customIcon,
  documentStatus,
  message,
  onAction,
  onDismiss,
  onRetry,
  primaryActionLabel,
  showDismissButton,
  styles,
  uploadError,
  variant,
}) => {
  const toastStyles = useToastStyles();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  const resolvedVariant =
    variant ??
    (() => {
      if (uploadError) {
        return "error";
      }
      if (
        documentStatus === "processing" ||
        documentStatus === "running" ||
        documentStatus === "pending" ||
        documentStatus === null
      ) {
        return "loading";
      }
      if (documentStatus === "failed") {
        return "error";
      }
      if (documentStatus === "finished" || documentStatus === "partiallyDone") {
        return "success";
      }
      return null;
    })();

  const resolvedMessage =
    message ??
    (() => {
      if (uploadError) {
        return uploadError.message || translate("Failed to upload document");
      }
      if (
        documentStatus === "processing" ||
        documentStatus === "running" ||
        documentStatus === "pending" ||
        documentStatus === null
      ) {
        return `${translate("Document is processing")}...`;
      }
      if (documentStatus === "failed") {
        return translate("Document processing failed.");
      }
      if (documentStatus === "finished") {
        return translate("Document context is ready");
      }
      return null;
    })();

  const resolvedShowDismissButton =
    showDismissButton ??
    (documentStatus === "finished" ||
      (uploadError !== null && uploadError !== undefined));
  const resolvedAutoDismiss = autoDismiss ?? documentStatus === "finished";
  const resolvedPrimaryActionLabel =
    primaryActionLabel ??
    (documentStatus === "failed" || uploadError
      ? translate("Try again")
      : undefined);
  const resolvedOnAction =
    onAction ??
    (documentStatus === "failed" || uploadError ? onRetry : undefined);

  const handleDismiss = useCallback(() => {
    setIsFadingOut(true);
    setTimeout(() => {
      setIsDismissed(true);
      onDismiss?.();
    }, 200);
  }, [onDismiss]);

  // Auto-dismiss toast if configured
  useEffect(() => {
    if (resolvedAutoDismiss && !isDismissed && !isFadingOut) {
      const timer = setTimeout(() => {
        setIsFadingOut(true);
        setTimeout(() => {
          setIsDismissed(true);
          onDismiss?.();
        }, 200);
      }, autoDismissDelay);

      return () => clearTimeout(timer);
    }
  }, [
    resolvedAutoDismiss,
    isDismissed,
    isFadingOut,
    autoDismissDelay,
    onDismiss,
  ]);

  if (isDismissed || !resolvedVariant) {
    return null;
  }

  const getIcon = () => {
    if (customIcon) {
      return customIcon;
    }

    switch (resolvedVariant) {
      case "error":
        return <DismissCircleRegular className={toastStyles.errorIcon} />;
      case "loading":
        return (
          <Spinner className={toastStyles.loadingIcon} size="extra-tiny" />
        );
      case "success":
        return <CheckmarkCircleFilled className={toastStyles.icon} />;
      default:
        return null;
    }
  };

  return (
    <div className={styles.processingIndicator}>
      <div
        className={`${toastStyles.toast} ${
          isFadingOut ? toastStyles.toastFadeOut : ""
        }`}
      >
        <div className={toastStyles.iconTitleWrapper}>
          <div className={toastStyles.iconWrapper}>{getIcon()}</div>
          <p className={toastStyles.title}>{resolvedMessage}</p>
        </div>

        {resolvedPrimaryActionLabel && resolvedOnAction && (
          <Button
            appearance="transparent"
            className={toastStyles.tryAgainButton}
            onClick={resolvedOnAction}
            size="small"
          >
            {resolvedPrimaryActionLabel}
          </Button>
        )}

        {resolvedShowDismissButton && (
          <Button
            appearance="transparent"
            aria-label="Dismiss"
            className={toastStyles.dismissButton}
            icon={<DismissRegular className={toastStyles.dismissIcon} />}
            onClick={handleDismiss}
            size="small"
          />
        )}
      </div>
    </div>
  );
};

const useToastStyles = makeStyles({
  dismissButton: {
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
    alignItems: "center",
    background: "none",
    border: "none",
    borderRadius: tokens.borderRadiusSmall,
    cursor: "pointer",
    display: "flex",
    justifyContent: "center",
    padding: 0,
  },
  dismissIcon: {
    color: tokens.colorNeutralForeground1,
    fontSize: "1.2rem",
  },
  errorIcon: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: "1.2rem",
  },
  icon: {
    color: tokens.colorPaletteGreenForeground1,
    fontSize: "1.2rem",
  },
  iconTitleWrapper: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    gap: tokens.spacingHorizontalS,
  },
  iconWrapper: {
    alignItems: "center",
    display: "flex",
    justifyContent: "center",
  },
  loadingIcon: {
    fontSize: "1rem",
  },
  title: {
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.lineHeightBase200,
    margin: 0,
  },
  toast: {
    alignItems: "center",
    animationDuration: tokens.durationNormal,
    animationFillMode: "forwards",
    animationName: "fadeIn",
    animationTimingFunction: tokens.curveEasyEase,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusCircular,
    boxShadow: tokens.shadow16,
    display: "flex",
    gap: tokens.spacingHorizontalM,
    minWidth: "18.25rem",
    paddingBlock: tokens.spacingVerticalS,
    paddingInline: tokens.spacingHorizontalM,
    position: "relative",
  },
  toastFadeOut: {
    animationName: "fadeOut",
  },
  tryAgainButton: {
    background: "none",
    border: "none",
    color: tokens.colorBrandForeground1,
    cursor: "pointer",
    padding: 0,
  },
});
